import { Project } from '../../types';
import { MetadataStore } from './metadata';
import { migrateProject } from '../migration';
import { ExampleProjectLoader } from '../import/examples';

export class ProjectStore {
  private static readonly DB_NAME = 'railmapper';
  private static readonly STORE_NAME = 'projects';
  private static readonly VERSION = 2;
  private static readonly DELETION_DELAY = 60 * 60 * 1000; // 1 hour in milliseconds
  private db: IDBDatabase | null = null;
  private cleanupInterval: number | null = null;

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(ProjectStore.DB_NAME, ProjectStore.VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        this.startCleanupInterval();
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(ProjectStore.STORE_NAME)) {
          db.createObjectStore(ProjectStore.STORE_NAME, { keyPath: 'id' });
        }
      };
    });
  }

  private startCleanupInterval(): void {
    // Run cleanup every 15 minutes
    this.cleanupInterval = window.setInterval(() => {
      this.cleanupDeletedProjects().catch(console.error);
    }, 15 * 60 * 1000);
  }

  public stopCleanupInterval(): void {
    if (this.cleanupInterval !== null) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
  }

  async getAllProjects(includeDeleted: boolean = false): Promise<Project[]> {
    if (!this.db) throw new Error('Database not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([ProjectStore.STORE_NAME], 'readonly');
      const store = transaction.objectStore(ProjectStore.STORE_NAME);
      const request = store.getAll();

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const projects = request.result;
        resolve(includeDeleted ? projects : projects.filter(p => !p.isDeleted));
      };
    });
  }

  async getActiveProjects(): Promise<{ userProjects: { id: string; name: string; lastEdited: number }[]; exampleProjects: { id: string; name: string; lastEdited: number; sourceFile?: string }[] }> {
    const projects = await this.getAllProjects(false);
    
    // Split projects into user and example
    const userProjects = projects
      .filter(p => p.type === 'user')
      .map(p => ({
        id: p.id,
        name: p.name,
        lastEdited: p.lastEdited
      }))
      .sort((a, b) => b.lastEdited - a.lastEdited);

    const exampleProjects = projects
      .filter(p => p.type === 'example')
      .map(p => ({
        id: p.id,
        name: p.name,
        lastEdited: p.lastEdited,
        sourceFile: p.sourceFile
      }))
      .sort((a, b) => {
        const getSortKey = (sourceFile: string | undefined) => {
          if (!sourceFile) return { numeric: null, alpha: '' };
          const numericMatch = sourceFile.match(/^(\d+)/);
          return {
            numeric: numericMatch ? parseInt(numericMatch[1]) : null,
            alpha: sourceFile
          };
        };

        const keyA = getSortKey(a.sourceFile);
        const keyB = getSortKey(b.sourceFile);

        if (keyA.numeric !== null && keyB.numeric !== null) {
          return keyA.numeric - keyB.numeric;
        }
        return keyA.alpha.localeCompare(keyB.alpha);
      });

    return { userProjects, exampleProjects };
  }

  async cleanupDeletedProjects(): Promise<void> {
    const projects = await this.getAllProjects(true);
    const now = Date.now();

    for (const project of projects) {
      if (project.isDeleted && project.deletedAt && 
          (now - project.deletedAt > ProjectStore.DELETION_DELAY)) {
        await this.hardDeleteProject(project.id);
      }
    }
  }

  async softDeleteProject(id: string, allowExampleDeletion: boolean = false): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');

    const project = await this.getProject(id);
    if (!project) throw new Error('Project not found');

    if (project.type === 'example' && !allowExampleDeletion) {
      console.warn('Attempted to delete example project:', { id, name: project.name });
      throw new Error('Cannot delete example projects');
    }

    project.isDeleted = true;
    project.deletedAt = Date.now();

    await this.saveProject(project);
    MetadataStore.deleteProject(id);
  }

  private async hardDeleteProject(id: string): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([ProjectStore.STORE_NAME], 'readwrite');
      const store = transaction.objectStore(ProjectStore.STORE_NAME);
      const request = store.delete(id);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  async saveProject(project: Project, updateTimestamp: boolean = true): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');

    if (updateTimestamp) {
      project.lastEdited = Date.now();
    }
    
    // Ensure project is migrated before saving
    const migratedProject = migrateProject(project);

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([ProjectStore.STORE_NAME], 'readwrite');
      const store = transaction.objectStore(ProjectStore.STORE_NAME);
      const request = store.put(migratedProject);

      request.onerror = () => {
        console.error('Failed to save project:', { 
          projectId: project.id, 
          identifier: project.identifier, 
          error: request.error 
        });
        reject(request.error);
      };
      request.onsuccess = () => {
        // Only update metadata if project is not deleted
        if (!migratedProject.isDeleted) {
          MetadataStore.updateProject({
            id: migratedProject.id,
            name: migratedProject.name,
            lastEdited: migratedProject.lastEdited,
            type: migratedProject.type,
            sourceFile: migratedProject.sourceFile
          });
        }
        resolve();
      };
    });
  }

  async getProject(id: string): Promise<Project | null> {
    if (!this.db) throw new Error('Database not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([ProjectStore.STORE_NAME], 'readonly');
      const store = transaction.objectStore(ProjectStore.STORE_NAME);
      const request = store.get(id);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const project = request.result || null;
        if (project) {
          // Migrate the project if needed
          const migratedProject = migrateProject(project);
          resolve(migratedProject);
        } else {
          resolve(null);
        }
      };
    });
  }

  // Alias deleteProject to softDeleteProject for backward compatibility
  async deleteProject(id: string): Promise<void> {
    return this.softDeleteProject(id);
  }

  async deleteAllProjects(): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');

    const projects = await this.getAllProjects(false);
    
    // Delete all user projects one by one
    for (const project of projects) {
      if (project.type === 'user') {
        await this.softDeleteProject(project.id);
      }
    }
  }

  async duplicateProject(id: string): Promise<Project> {
    const project = await this.getProject(id);
    if (!project) throw new Error('Project not found');

    // Get all projects to find similar names
    const allProjects = await this.getAllProjects(false);
    const baseNameMatch = project.name.match(/^(.*?)(?:\s+v\d+)?$/);
    const baseName = baseNameMatch ? baseNameMatch[1] : project.name;
    
    // Find all projects with the same base name and get their version numbers
    const versionNumbers = allProjects
      .map(p => {
        const match = p.name.match(new RegExp(`^${baseName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s+v(\\d+)$`));
        return match ? parseInt(match[1], 10) : 0;
      })
      .filter(v => v > 0);

    // Calculate the new version number
    const highestVersion = versionNumbers.length > 0 ? Math.max(...versionNumbers) : 1;
    const newName = `${baseName} v${highestVersion + 1}`;

    const newProject: Project = {
      ...project,
      id: crypto.randomUUID(),
      identifier: '', // Set empty identifier to trigger generation of a new one
      name: newName,
      lastEdited: Date.now(),
      type: 'user'  // Duplicated projects are always user projects
    };

    // Ensure the project gets a new unique identifier
    const projectWithIdentifier = await this.ensureProjectIdentifier(newProject);
    
    // Save the new project with timestamp update
    await this.saveProject(projectWithIdentifier, true);
    
    // Save the original project without timestamp update
    await this.saveProject(project, false);
    
    return projectWithIdentifier;
  }

  async handleExampleEdit(project: Project): Promise<Project> {
    if (project.type !== 'example' || !project.sourceFile) {
      throw new Error('Invalid project type or missing source file');
    }

    console.debug('Handling example project edit:', {
      id: project.id,
      name: project.name,
      sourceFile: project.sourceFile
    });

    // 1. Save the edited example first
    await this.saveProject(project, true);

    // 2. Create user copy with version (this will include the edit)
    const userProject = await this.duplicateProject(project.id);

    // 3. Reset the original example to pristine state
    const success = await ExampleProjectLoader.resetExampleProject(project.id, this);
    if (!success) {
      console.error('Failed to reset example project');
      throw new Error('Failed to reset example project');
    }

    console.debug('Example project edit handled:', {
      originalId: project.id,
      userCopyId: userProject.id
    });

    return userProject;
  }

  async generateProjectIdentifier(projectName: string): Promise<string> {
    const randomBuffer = new Uint32Array(1);
    crypto.getRandomValues(randomBuffer);
    const input = `${projectName}${Date.now()}${randomBuffer[0]}`;
    const hash = await crypto.subtle.digest('SHA-1', new TextEncoder().encode(input));
    const identifier = Array.from(new Uint8Array(hash))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('')
      .slice(0, 6);
    return identifier;
  }

  async isIdentifierUnique(identifier: string): Promise<boolean> {
    const projects = await this.getAllProjects(false);
    return !projects.some(p => p.identifier === identifier);
  }

  async loadProjectByIdentifier(identifier: string): Promise<Project | null> {
    const projects = await this.getAllProjects(false);
    const project = projects.find(p => p.identifier === identifier);
    if (!project) {
      return null;
    }
    return this.getProject(project.id);
  }

  async ensureProjectIdentifier(project: Project): Promise<Project> {
    if (project.identifier) {
      return project;
    }

    let identifier: string;
    do {
      identifier = await this.generateProjectIdentifier(project.name);
    } while (!(await this.isIdentifierUnique(identifier)));

    const updatedProject = {
      ...project,
      identifier
    };

    await this.saveProject(updatedProject);
    return updatedProject;
  }
}